import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'src/utils/baseAxios';

/* eslint-disable no-param-reassign */

const getErrorMessage = (error, fallback) => error.response?.data?.message
  || error.response?.data?.Message
  || error.response?.data
  || error.message
  || fallback;

export const getPakistanManualDiscountRegions = createAsyncThunk(
  'pakistanManualDiscount/getRegions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/PakistanManualDiscount/GetRegions');
      return response.data || [];
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to load regions'));
    }
  }
);

export const searchPakistanManualDiscount = createAsyncThunk(
  'pakistanManualDiscount/search',
  async (request, { rejectWithValue }) => {
    try {
      const response = await axios.post('/PakistanManualDiscount/Search', request);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Search failed'));
    }
  }
);

export const getPakistanManualDiscountDetails = createAsyncThunk(
  'pakistanManualDiscount/getDetails',
  async (workOrderId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/PakistanManualDiscount/GetDetailsWithApproval/${workOrderId}`);
      return response.data || { Details: [] };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to load discount details'));
    }
  }
);

export const approvePakistanManualDiscount = createAsyncThunk(
  'pakistanManualDiscount/approve',
  async (request, { rejectWithValue }) => {
    try {
      const response = await axios.post('/PakistanManualDiscount/Approve', request);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Approval failed'));
    }
  }
);

export const evaluatePakistanManualDiscountApproval = createAsyncThunk(
  'pakistanManualDiscount/evaluateApproval',
  async (request, { rejectWithValue }) => {
    try {
      const response = await axios.post('/PakistanManualDiscount/EvaluateApproval', request);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Approval evaluation failed'));
    }
  }
);

export const rejectPakistanManualDiscount = createAsyncThunk(
  'pakistanManualDiscount/reject',
  async (request, { rejectWithValue }) => {
    try {
      const response = await axios.post('/PakistanManualDiscount/Reject', request);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Rejection failed'));
    }
  }
);

const pakistanManualDiscountSlice = createSlice({
  name: 'pakistanManualDiscount',
  initialState: {
    loading: false,
    actionLoading: false,
    evaluationLoading: false,
    evaluationRequestId: null,
    regions: [],
    rows: [],
    totalCount: 0,
    details: [],
    approvalInfo: null,
    error: ''
  },
  reducers: {
    clearPakistanManualDiscountError(state) {
      state.error = '';
    },
    clearPakistanManualDiscountDetails(state) {
      state.details = [];
      state.approvalInfo = null;
      state.evaluationLoading = false;
      state.evaluationRequestId = null;
    },
    clearPakistanManualDiscountRows(state) {
      state.rows = [];
      state.totalCount = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPakistanManualDiscountRegions.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(getPakistanManualDiscountRegions.fulfilled, (state, action) => {
        state.loading = false;
        state.regions = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getPakistanManualDiscountRegions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(searchPakistanManualDiscount.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(searchPakistanManualDiscount.fulfilled, (state, action) => {
        state.loading = false;
        state.rows = action.payload?.Items || [];
        state.totalCount = action.payload?.TotalCount || 0;
      })
      .addCase(searchPakistanManualDiscount.rejected, (state, action) => {
        state.loading = false;
        state.rows = [];
        state.totalCount = 0;
        state.error = action.payload || action.error.message;
      })
      .addCase(getPakistanManualDiscountDetails.pending, (state) => {
        state.actionLoading = true;
        state.error = '';
        state.details = [];
        state.approvalInfo = null;
        state.evaluationLoading = false;
        state.evaluationRequestId = null;
      })
      .addCase(getPakistanManualDiscountDetails.fulfilled, (state, action) => {
        state.actionLoading = false;
        // Keep compatibility with the previous API response while deployments
        // are rolling out. The new response wraps rows with approval metadata.
        if (Array.isArray(action.payload)) {
          state.details = action.payload;
          state.approvalInfo = null;
        } else {
          state.details = action.payload?.Details || [];
          state.approvalInfo = action.payload || null;
        }
      })
      .addCase(getPakistanManualDiscountDetails.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(evaluatePakistanManualDiscountApproval.pending, (state, action) => {
        state.evaluationLoading = true;
        state.evaluationRequestId = action.meta.requestId;
        state.error = '';
      })
      .addCase(evaluatePakistanManualDiscountApproval.fulfilled, (state, action) => {
        if (state.evaluationRequestId !== action.meta.requestId) return;
        state.evaluationLoading = false;
        state.evaluationRequestId = null;
        state.approvalInfo = {
          ...(state.approvalInfo || {}),
          ...(action.payload || {})
        };
      })
      .addCase(evaluatePakistanManualDiscountApproval.rejected, (state, action) => {
        if (state.evaluationRequestId !== action.meta.requestId) return;
        state.evaluationLoading = false;
        state.evaluationRequestId = null;
        state.error = action.payload || action.error.message;
      })
      .addCase(approvePakistanManualDiscount.pending, (state) => {
        state.actionLoading = true;
        state.error = '';
      })
      .addCase(approvePakistanManualDiscount.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(approvePakistanManualDiscount.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(rejectPakistanManualDiscount.pending, (state) => {
        state.actionLoading = true;
        state.error = '';
      })
      .addCase(rejectPakistanManualDiscount.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(rejectPakistanManualDiscount.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || action.error.message;
      });
  }
});

export const {
  clearPakistanManualDiscountError,
  clearPakistanManualDiscountDetails,
  clearPakistanManualDiscountRows
} = pakistanManualDiscountSlice.actions;

export default pakistanManualDiscountSlice;
